/**
 * Chat Redux Slice
 */
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import chatApi, {
  SendMessageRequest,
  UploadFileInChatRequest,
} from "../../api/chat";

import { ChatMessage, ChatSession, ChatHistory, MessageRole } from "../../types/chat.types";

interface ChatState {
  currentSession: ChatSession | null;
  sessions: ChatSession[];
  messages: ChatMessage[];
  loading: boolean;
  sending: boolean;
  uploading: boolean;
  error: string | null;
  totalSessions: number;
  pendingSessionId: string | null;  // NEW: UUID for new chat before first message
}

const initialState: ChatState = {
  currentSession: null,
  sessions: [],
  messages: [],
  loading: false,
  sending: false,
  uploading: false,
  error: null,
  totalSessions: 0,
  pendingSessionId: null,
};

// Async thunks
export const sendMessage = createAsyncThunk(
  "chat/sendMessage",
  async (data: SendMessageRequest, { rejectWithValue }) => {
    try {
      const response = await chatApi.sendMessage(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || "Failed to send message"
      );
    }
  }
);

export const uploadFileInChat = createAsyncThunk(
  "chat/uploadFile",
  async (data: UploadFileInChatRequest, { rejectWithValue }) => {
    try {
      const response = await chatApi.uploadFile(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || "Failed to upload file"
      );
    }
  }
);

export const fetchSessions = createAsyncThunk(
  "chat/fetchSessions",
  async (
    { page = 1, page_size = 20 }: { page?: number; page_size?: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await chatApi.getSessions(page, page_size);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || "Failed to fetch sessions"
      );
    }
  }
);

export const fetchHistory = createAsyncThunk(
  "chat/fetchHistory",
  async (session_id: string, { rejectWithValue }) => {
    try {
      const response = await chatApi.getHistory(session_id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || "Failed to fetch history"
      );
    }
  }
);

export const deleteSession = createAsyncThunk(
  "chat/deleteSession",
  async (session_id: string, { rejectWithValue }) => {
    try {
      await chatApi.deleteSession(session_id);
      return session_id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || "Failed to delete session"
      );
    }
  }
);

export const closeSession = createAsyncThunk(
  "chat/closeSession",
  async (session_id: string, { rejectWithValue }) => {
    try {
      await chatApi.closeSession(session_id);
      return session_id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || "Failed to close session"
      );
    }
  }
);

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentSession: (state, action: PayloadAction<ChatSession | null>) => {
      state.currentSession = action.payload;
      // Clear messages when switching sessions - they will be loaded fresh
      state.messages = [];
    },
    addUserMessage: (
      state,
      action: PayloadAction<{ content: string; session_id: string }>
    ) => {
      // Optimistically add user message
      const tempMessage: ChatMessage = {
        id: `temp-${Date.now()}`,
        session_id: action.payload.session_id,
        role: MessageRole.USER,  // Use proper enum value
        content: action.payload.content,
        created_at: new Date().toISOString(),
      };
      state.messages.push(tempMessage);
    },
    clearMessages: (state) => {
      state.messages = [];
    },
    startNewSession: (state) => {
      // Generate unique UUID for new session
      state.pendingSessionId = crypto.randomUUID();
      state.currentSession = null;
      state.messages = [];
      state.error = null;
    },
    clearPendingSession: (state) => {
      state.pendingSessionId = null;
    },
  },
  extraReducers: (builder) => {
    // Send message
    builder.addCase(sendMessage.pending, (state) => {
      state.sending = true;
      state.error = null;
    });
    builder.addCase(sendMessage.fulfilled, (state, action) => {
      state.sending = false;
      // Add assistant message
      state.messages.push(action.payload);

      // If no current session but response has session_id, create/set current session
      if (!state.currentSession && action.payload.session_id) {
        const newSession = {
          id: action.payload.session_id,
          user_id: "", // Will be populated on session fetch
          title: action.payload.metadata?.session_title || "Untitled Chat",
          is_active: true,
          message_count: 1,
          created_at: action.payload.created_at,
          last_message_at: action.payload.created_at,
          llm_provider: null,
          llm_model: null,
        };
        state.currentSession = newSession;
        
        // Add to sessions list so it appears in sidebar immediately
        // Add at the beginning (most recent first)
        state.sessions.unshift(newSession);
        state.totalSessions += 1;
      }
      // Update current session if it exists
      else if (
        state.currentSession &&
        state.currentSession.id === action.payload.session_id
      ) {
        state.currentSession.last_message_at = action.payload.created_at;
        state.currentSession.message_count = (state.currentSession.message_count || 0) + 1;
        
        // Also update in sessions list
        const sessionIndex = state.sessions.findIndex(s => s.id === state.currentSession?.id);
        if (sessionIndex !== -1) {
          state.sessions[sessionIndex].last_message_at = action.payload.created_at;
          state.sessions[sessionIndex].message_count = state.currentSession.message_count;
          // Update title if new title is provided
          if (action.payload.metadata?.session_title) {
            state.sessions[sessionIndex].title = action.payload.metadata.session_title;
            state.currentSession.title = action.payload.metadata.session_title;
          }
        }
      }
    });

    builder.addCase(sendMessage.rejected, (state, action) => {
      state.sending = false;
      state.error = action.payload as string;
    });
    // Upload file
    builder.addCase(uploadFileInChat.pending, (state) => {
      state.uploading = true;
      state.error = null;
    });
    builder.addCase(uploadFileInChat.fulfilled, (state, action) => {
      state.uploading = false;
      // File upload creates a job, response handled separately
    });
    builder.addCase(uploadFileInChat.rejected, (state, action) => {
      state.uploading = false;
      state.error = action.payload as string;
    });
    // Fetch sessions
    builder.addCase(fetchSessions.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchSessions.fulfilled, (state, action) => {
      state.loading = false;
      state.sessions = action.payload.sessions;
      state.totalSessions = action.payload.total;
    });
    builder.addCase(fetchSessions.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    // Fetch history
    builder.addCase(fetchHistory.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchHistory.fulfilled, (state, action) => {
      state.loading = false;
      state.currentSession = action.payload.session;
      state.messages = action.payload.messages;
    });
    builder.addCase(fetchHistory.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    // Delete session
    builder.addCase(deleteSession.fulfilled, (state, action) => {
      state.sessions = state.sessions.filter((s) => s.id !== action.payload);
      if (state.currentSession?.id === action.payload) {
        state.currentSession = null;
        state.messages = [];
      }
    });
    // Close session
    builder.addCase(closeSession.fulfilled, (state, action) => {
      // Mark session as inactive
      const session = state.sessions.find((s) => s.id === action.payload);
      if (session) {
        session.is_active = false;
      }
      if (state.currentSession?.id === action.payload) {
        state.currentSession.is_active = false;
      }
    });
  },
});

export const { clearError, setCurrentSession, addUserMessage, clearMessages, startNewSession, clearPendingSession } =
  chatSlice.actions;

export default chatSlice.reducer;
